import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Link } from 'react-router-dom';

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      {/* Hero Section with Background Image */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img
            src="/images/how-it-works-hero.jpg"
            alt="How It Works"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
        <div className="relative h-full flex flex-col justify-end max-w-7xl mx-auto px-6 lg:px-8 pb-12">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-4">
            How It Works
          </h1>
          <p className="text-white/90 text-lg max-w-2xl">
            Planning a trip to Italy shouldn't feel like a second job. We cut through the noise with curated recommendations from someone who actually lives here.
          </p>
        </div>
      </section>

      <main className="bg-[#FAFAFA]">

      {/* Steps Section */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <p className="text-[#C86A2A] text-sm font-semibold uppercase tracking-wider mb-4">
            Simple, not overwhelming
          </p>
          <h2 className="font-serif text-4xl md:text-5xl mb-4">
            Get exactly what you need—no more, no less
          </h2>
        </div>

        <div className="space-y-20 mt-20">
          {/* Step 1 */}
          <div className="grid md:grid-cols-[80px_1fr] gap-10 items-start">
            <div className="w-20 h-20 rounded-full border-2 border-gray-200 flex items-center justify-center bg-white mx-auto md:mx-0">
              <span className="font-serif text-3xl text-[#C86A2A] font-semibold">1</span>
            </div>
            <div className="text-center md:text-left">
              <h3 className="font-serif text-3xl mb-4">Pick your destination</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Choose from Rome, Florence, Venice, the Dolomites, Cinque Terre, or the Amalfi Coast. Each guide is specifically crafted for that city, with the insider knowledge you need to experience it like a local.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="grid md:grid-cols-[80px_1fr] gap-10 items-start">
            <div className="w-20 h-20 rounded-full border-2 border-gray-200 flex items-center justify-center bg-white mx-auto md:mx-0">
              <span className="font-serif text-3xl text-[#C86A2A] font-semibold">2</span>
            </div>
            <div className="text-center md:text-left">
              <h3 className="font-serif text-3xl mb-4">Read the free basics</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Get an overview of must-sees and best times to visit—completely free. This gives you a taste of the destination and helps you decide if you want the full insider experience.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="grid md:grid-cols-[80px_1fr] gap-10 items-start">
            <div className="w-20 h-20 rounded-full border-2 border-gray-200 flex items-center justify-center bg-white mx-auto md:mx-0">
              <span className="font-serif text-3xl text-[#C86A2A] font-semibold">3</span>
            </div>
            <div className="text-center md:text-left">
              <h3 className="font-serif text-3xl mb-4">Go deeper if you want</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Unlock the full guide for restaurant picks, hidden gems, and local secrets. Or get a custom itinerary built just for you. Your choice—we're here to help, not overwhelm.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="bg-white py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C86A2A] text-sm font-semibold uppercase tracking-wider mb-4">
              What makes us different
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mb-4">
              Authentic insider knowledge
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Not just another travel blog written by someone who visited for a weekend
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mt-16">
            {/* Tourist Guidebooks */}
            <div>
              <h4 className="text-xl font-medium mb-8 pb-4 border-b-2 border-gray-200">
                Tourist Guidebooks
              </h4>
              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✗
                  </div>
                  <p className="text-gray-600">Generic recommendations everyone knows about</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✗
                  </div>
                  <p className="text-gray-600">Written by non-Italian travel writers</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✗
                  </div>
                  <p className="text-gray-600">Tourist traps disguised as recommendations</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✗
                  </div>
                  <p className="text-gray-600">Same experience as every other tourist</p>
                </div>
              </div>
            </div>

            {/* Italian Insiders */}
            <div>
              <h4 className="text-xl font-medium mb-8 pb-4 border-b-2 border-[#C86A2A]">
                Italian Insiders
              </h4>
              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <p className="text-gray-600">Hidden gems only locals know</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <p className="text-gray-600">Created by Italian-born insider</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <p className="text-gray-600">Authentic restaurants locals actually frequent</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">
                    ✓
                  </div>
                  <p className="text-gray-600">Unique experiences off the beaten path</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-gray-50 py-20 px-6">
        <div className="max-w-xl mx-auto bg-white rounded-2xl p-12 md:p-16 border border-gray-200 text-center">
          <h3 className="font-serif text-4xl mb-2">Full City Guide</h3>
          <div className="inline-block bg-orange-50 text-[#C86A2A] px-4 py-1.5 rounded-full text-sm font-semibold mb-8">
            One-time payment
          </div>
          <div className="font-serif text-6xl my-6">$199</div>
          <p className="text-gray-500 mb-10">Lifetime access • No subscriptions</p>

          <ul className="text-left space-y-3 mb-10">
            {[
              '10+ restaurant recommendations',
              'Hidden gems & local secrets',
              'Off-the-beaten-path spots',
              'Neighborhood deep-dives',
              'Day trip suggestions',
              'Practical tips & timing',
              'Access from any device'
            ].map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-[#C86A2A] font-semibold flex-shrink-0">✓</span>
                <span className="text-gray-600">{feature}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/destinations"
            className="inline-block bg-black text-white px-12 py-4 rounded-full font-medium hover:bg-gray-900 transition-all"
          >
            Choose Your Destination
          </Link>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <p className="text-[#C86A2A] text-sm font-semibold uppercase tracking-wider mb-4">
            Questions?
          </p>
          <h2 className="font-serif text-4xl md:text-5xl">Common questions</h2>
        </div>

        <div className="space-y-5">
          {[
            {
              q: 'How do I access my guide after purchasing?',
              a: "You'll receive an email with login credentials immediately after purchase. Access your guide anytime by logging into your account."
            },
            {
              q: 'Can I purchase guides for multiple cities?',
              a: "Absolutely. Each city guide is $199, and you can purchase as many as you'd like from your dashboard."
            },
            {
              q: "What if I've never been to Italy before?",
              a: 'Perfect. Our guides work for both first-timers and seasoned travelers. We include everything from basics to advanced insider knowledge.'
            },
            {
              q: 'How is this different from free travel blogs?',
              a: "Free blogs are written by tourists visiting for a few days. Italian Insiders comes from someone born and raised in Italy, with genuine local connections and insider knowledge you can't get from a tourist's perspective."
            },
            {
              q: 'Do you offer refunds?',
              a: "Yes. 7-day money-back guarantee. If you're not satisfied, contact us within 7 days for a full refund."
            }
          ].map((faq, index) => (
            <div key={index} className="bg-white rounded-xl p-8 border border-gray-200">
              <h4 className="text-xl font-medium mb-3">{faq.q}</h4>
              <p className="text-gray-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-white py-24 px-6 text-center">
        <h2 className="font-serif text-4xl md:text-5xl mb-5">
          Ready to experience authentic Italy?
        </h2>
        <p className="text-lg text-gray-600 mb-10">
          Choose your destination and get instant access to insider knowledge
        </p>
        <Link
          to="/destinations"
          className="inline-block bg-black text-white px-12 py-4 rounded-full font-medium hover:bg-gray-900 transition-all"
        >
          Browse Destinations
        </Link>
      </div>
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorksPage;

